import { Box, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { SummaryStatistics } from '~/domain/statistics/summaryStatistics';
import { SUMMARY_STAT_LABEL_IDS } from '~/utils/summaryStatLabels';

/** Read left to right: the two extremes, then the centre and its spread. */
const SHOWN_KEYS = ['min', 'max', 'mean', 'std'] as const;

/** The figure the shared unit is stated after. */
const UNIT_CARRIER = SHOWN_KEYS[SHOWN_KEYS.length - 1];

interface SummaryStatsRowProps {
    stats: SummaryStatistics;
    /**
     * Formats a value for display, without the unit. The same function is
     * used for all four figures so they land on one scale; a formatter that
     * chose its own prefix per value could show the mean in kBq and the SD in
     * Bq.
     */
    format: (value: number) => string;
    /** Shown once at the end, since all four share it by construction. */
    unit: string;
}

/**
 * min / max / mean / SD on one line across the full width, with the unit
 * written once, after the last figure. Repeating it four times makes the row
 * wrap and splits labels from their numbers; putting it in an element of its
 * own leaves it alone at the far edge. Each label stays in one element with
 * its number, so the two never separate.
 */
export default function SummaryStatsRow({
    stats,
    format,
    unit,
}: SummaryStatsRowProps) {
    const intl = useIntl();
    return (
        <Stack
            direction="row"
            alignItems="baseline"
            sx={{
                columnGap: 1.25,
                rowGap: 0.2,
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                mt: 0.25,
            }}
        >
            {SHOWN_KEYS.map((key) => (
                <Typography
                    key={key}
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {intl.formatMessage({ id: SUMMARY_STAT_LABEL_IDS[key] })}{' '}
                    <Box
                        component="span"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                        {format(stats[key])}
                    </Box>
                    {unit && key === UNIT_CARRIER && ` ${unit}`}
                </Typography>
            ))}
        </Stack>
    );
}
